import { useCallback, useEffect, useRef, useState } from 'react';
import { AUDIT_ACTIONS, formatDateTime } from '@pr-app/shared';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Tab,
  TabList,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useNavigate } from 'react-router-dom';
import { useTeams } from '../teams/TeamsProvider';
import { ActionBar } from '../components/ActionBar';
import { ApiErrorMessage } from '../components/ApiErrorMessage';
import { AuditActionBadge, auditActionLabel } from '../components/AuditActionBadge';
import { DataTable, type Column } from '../components/DataTable';
import { EmptyState } from '../components/EmptyState';
import { FilterBar } from '../components/FilterBar';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { NotificationTemplatesSection } from './NotificationTemplates';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { SPACE, SPACE_X } from '../styles/scale';
import { useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';
import {
  addAdministrator,
  addReviewer,
  exportAuditLog,
  getAdministrators,
  getAppSettings,
  getAuditLog,
  getD365Status,
  getReviewers,
  removeAdministrator,
  removeReviewer,
  searchDirectory,
  testD365Connection,
  updateAppSettings,
  type Administrator,
  type AppSettings,
  type AppSettingsUpdate,
  type AuditEntry,
  type AuditLogPage,
  type D365ConnectionResult,
  type D365Status,
  type DirectoryUser,
  type Reviewer,
} from '../api/admin';
import { fromDateOnly, toDateOnly } from '../lib/dateOnly';

/**
 * Reviewer group management (#50, wireframe sheet 06 A1), the audit log
 * (#51, wireframe A4) and application settings plus the D365 connection panel
 * (#52, wireframe A3) and the notification templates (#49, wireframe A2 —
 * `NotificationTemplates.tsx`).
 *
 * ── The design-system pass (#202) ──
 * The page is one `PageHeader` (the app's only `<h1>` on this route) carrying
 * the secondary `TabList`, and every block below it is a `Section` with a real
 * `<h2>`. Before this, all five blocks were a bare `Subtitle2` — a `<span>`
 * that looks like a heading and navigates like body text — sitting on a
 * `display: flex` div with no surface at all.
 *
 * ── When a Section takes the card, and when it does not ──
 * `Section` defaults to the shared card (`useSurfaceStyles().card`, E1), and
 * that is what the blocks whose content is plain get: App settings, D365
 * connection, and each add-a-person picker. A block whose content is ITSELF a
 * card — a `DataTable` (`tableCard`), a `FilterBar`, a template editor —
 * passes `surface={false}` instead, because E1 nested inside E1 is the
 * double-surface the elevation scale forbids: the inner card would read as a
 * raised panel rather than as the section's content. So the heading and its
 * description sit on the canvas and the primitives below carry the surface.
 */

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

const BYTES_PER_MB = 1024 * 1024;

/**
 * Mirrors the bounds the API enforces (`lib/appSettings.ts`).
 *
 * Duplicated deliberately, and only to give immediate feedback: the server
 * validates the same ranges and answers 400, so a drift here is a worse error
 * message, never a setting that should not have been accepted.
 */
const SETTING_BOUNDS = {
  draftExpiryDays: { min: 1, max: 365 },
  maxAttachmentSizeMb: { min: 1, max: 50 },
  maxAttachmentsPerRequest: { min: 1, max: 50 },
} as const;

/**
 * A comment can be 2000 characters (the column width). Anything past this is
 * truncated in the cell, with the whole thing on the row's tooltip — a table
 * that reflows around one long rejection reason is unreadable.
 */
const COMMENT_PREVIEW_CHARS = 60;

/**
 * The narrowest a settings field may get before the row wraps. A width, not a
 * gap — it is on the 4px grid but it is not a spacing decision, so it is named
 * here rather than taken from `SPACE`, the same way `FilterBar` names its own.
 */
const SETTINGS_FIELD_MIN_WIDTH = '200px';

const useStyles = makeStyles({
  /** The add-a-person picker plus its own search/add progress and errors. */
  addRow: { display: 'flex', flexDirection: 'column', gap: SPACE.s },

  /**
   * A message inside a `DialogContent`, which has no stack of its own — the
   * only place left that needs an outer margin, because everything else on
   * this page sits in `useLayoutStyles().stack`.
   */
  dialogMessage: { marginTop: SPACE.s },

  meta: { color: tokens.colorNeutralForeground2 },

  /** Wraps to a single column on a narrow viewport rather than scrolling sideways. */
  settingsFields: { display: 'flex', flexWrap: 'wrap', gap: SPACE_X.l },
  settingsField: { minWidth: SETTINGS_FIELD_MIN_WIDTH, flexGrow: 1 },

  /** Label ↔ value ↔ badge, one fact per row. */
  facts: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: SPACE.s },
  fact: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: SPACE_X.s },

  /** Audit log's Actor cell (#219): name, then the raw Entra id as small secondary text. */
  actorCell: { display: 'flex', flexDirection: 'column', gap: SPACE.xs, minWidth: '0' },

  /**
   * The destructive confirm, using exactly the tokens `ActionBar`'s own
   * `destructive` rule uses (#202). `appearance="primary"` on a destroy is
   * banned: with a saturated indigo primary, the loudest and most inviting
   * control on a "Remove this person?" dialog would be the button that removes
   * them. `ActionBar` itself cannot be used here because K1 requires initial
   * focus on Cancel, and the bar owns its own buttons — so the treatment is
   * borrowed rather than the component.
   *
   * `border`, NOT `borderColor`: Griffel rejects `borderColor` as an
   * unsupported shorthand and DROPS the rule, so a red-bordered button written
   * that way is silently a grey-bordered one. Hover and pressed restate it, or
   * Fluent's own `:hover` pulls the colour back to neutral mid-gesture.
   */
  destructiveConfirm: {
    color: tokens.colorPaletteRedForeground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    ':hover': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
    ':hover:active': {
      color: tokens.colorPaletteRedForeground1,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorPaletteRedBorder2}`,
    },
  },
});

type LoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; reviewers: Reviewer[] }
  | { phase: 'error'; error: unknown };

/** Same shape as `LoadState` above, for the Administrators table (#156). */
type AdminLoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; administrators: Administrator[] }
  | { phase: 'error'; error: unknown };

type SearchState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; results: DirectoryUser[] }
  | { phase: 'error' };

type AuditState =
  | { phase: 'loading' }
  | { phase: 'ready'; result: AuditLogPage }
  | { phase: 'error'; error: unknown };

/**
 * What the table is showing: the applied (debounced for `q`, immediate for
 * the rest) filters plus the page within them (#156, wireframe A4's
 * `Action:` and date-range filters, alongside the pre-existing search).
 */
interface AuditView {
  q: string;
  /** Empty string means "every action". */
  action: string;
  /** Empty string means "no lower bound". YYYY-MM-DD. */
  from: string;
  /** Empty string means "no upper bound". YYYY-MM-DD. */
  to: string;
  page: number;
}

/**
 * The audit log (#51, wireframe A4).
 *
 * Paging and search are server-side — the log is insert-only and only ever
 * grows, so it is never fetched whole. System events (PR created) and human
 * ones (approved, reviewer added) render as the same row: one trail, not two
 * lists. There is no edit or delete affordance anywhere here, by design.
 *
 * It is also the widest table in the app, which is why it is a `DataTable`
 * (#202): Fluent's `Table` is `table-layout: fixed`, so at a 900px canvas — a
 * Teams tab with the side chat open — the overflow lands inside the CELLS,
 * where the card's `overflow: hidden` swallowed it with no scrollbar anywhere.
 * `DataTable` puts a scroll element between the card and the table so nothing
 * can be cut off, and derives the mobile cards from the same column array
 * instead of a second, hand-maintained list of labels.
 */
function AuditLogSection() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [view, setView] = useState<AuditView>({ q: '', action: '', from: '', to: '', page: 1 });
  const [state, setState] = useState<AuditState>({ phase: 'loading' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Debounce typing into the applied search. A new term always restarts at page
  // 1 — page 4 of the previous search means nothing for this one. The other
  // filters (Action, date range) apply immediately below, since they come
  // from a discrete pick rather than keystrokes.
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = query.trim();
      setView((current) => (current.q === trimmed ? current : { ...current, q: trimmed, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  /** Wireframe A4's `Action:` and date-range filters — set on selection, always back to page 1. */
  function updateFilter(patch: Partial<Pick<AuditView, 'action' | 'from' | 'to'>>) {
    setView((current) => ({ ...current, ...patch, page: 1 }));
  }

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const result = await getAuditLog({
        q: view.q || undefined,
        action: view.action || undefined,
        from: view.from || undefined,
        to: view.to || undefined,
        page: view.page,
      });
      setState({ phase: 'ready', result });
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  }, [view]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Exports the applied filter — never the whole table, and never just this page. */
  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportAuditLog({
        q: view.q || undefined,
        action: view.action || undefined,
        from: view.from || undefined,
        to: view.to || undefined,
      });
      downloadBlob(blob, 'audit-log.xlsx');
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  const result = state.phase === 'ready' ? state.result : undefined;
  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;
  const isFiltered = Boolean(view.q || view.action || view.from || view.to);

  /**
   * One definition, both faces. `priority` orders the mobile card so the top of
   * it answers what the row is — when, then what happened, then who — rather
   * than replaying the desktop column order.
   */
  const columns: Column<AuditEntry>[] = [
    {
      key: 'when',
      header: 'When',
      priority: 6,
      render: (entry) => formatDateTime(entry.createdAt),
    },
    {
      key: 'action',
      header: 'Action',
      priority: 5,
      render: (entry) => <AuditActionBadge action={entry.action} />,
    },
    {
      key: 'actor',
      header: 'Actor',
      priority: 4,
      render: (entry) => (
        <div className={styles.actorCell}>
          <span>{entry.actorName}</span>
          {/* Present on the type since #156 but never rendered — small
              secondary text rather than its own column, to keep the table
              from getting wider still (#219). */}
          <Caption1 className={styles.meta}>{entry.actorAadId}</Caption1>
        </div>
      ),
    },
    {
      key: 'actorEmail',
      header: 'Actor email',
      priority: 3,
      render: (entry) => entry.actorEmail ?? '—',
    },
    {
      key: 'request',
      header: 'Request',
      priority: 2,
      render: (entry) => <RequestCell entry={entry} onOpen={(path) => void navigate(path)} />,
    },
    {
      key: 'comment',
      header: 'Comment',
      priority: 1,
      render: (entry) => <AuditComment entry={entry} />,
    },
  ];

  return (
    <Section
      title="Audit log"
      description="Every request and administrative action, newest first. Entries can never be edited or removed."
      surface={false}
      action={
        // Subtle, not secondary: `Section`'s action sits on the heading line and
        // must not compete with the tab's own emphasis — and an export is a
        // side errand, not the reason anyone opened this screen.
        <Button appearance="subtle" disabled={exporting} onClick={() => void handleExport()}>
          {exporting ? <Spinner size="tiny" label="Exporting…" labelPosition="after" /> : 'Export'}
        </Button>
      }
    >
      <div className={layout.stack}>
        <FilterBar
          search={{
            label: 'Search',
            value: query,
            placeholder: 'Actor, comment or action…',
            onChange: setQuery,
          }}
        >
          <Field label="Action">
            <Dropdown
              placeholder="All actions"
              selectedOptions={view.action ? [view.action] : []}
              value={view.action ? auditActionLabel(view.action) : ''}
              onOptionSelect={(_, data) => updateFilter({ action: data.optionValue ?? '' })}
            >
              <Option value="">All actions</Option>
              {AUDIT_ACTIONS.map((action) => (
                <Option key={action} value={action}>
                  {auditActionLabel(action)}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="From">
            <DatePicker
              placeholder="From"
              value={view.from ? fromDateOnly(view.from) : null}
              onSelectDate={(date) => updateFilter({ from: date ? toDateOnly(date) : '' })}
            />
          </Field>
          <Field label="To">
            <DatePicker
              placeholder="To"
              value={view.to ? fromDateOnly(view.to) : null}
              onSelectDate={(date) => updateFilter({ to: date ? toDateOnly(date) : '' })}
            />
          </Field>
        </FilterBar>

        {exportError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not export the audit log</MessageBarTitle>
              {exportError}
            </MessageBarBody>
          </MessageBar>
        )}

        {state.phase === 'loading' && (
          // Two-second rule: this is a network call on the render path.
          <Spinner label="Loading audit log…" labelPosition="after" />
        )}

        {state.phase === 'error' && (
          // An error is never an empty state (10.2 E3): "nothing has been
          // logged" and "we could not read the log" are different facts.
          <ApiErrorMessage
            error={state.error}
            title="Could not load the audit log"
            onRetry={() => void load()}
          />
        )}

        {result &&
          result.entries.length === 0 &&
          (isFiltered ? (
            <EmptyState variant="no-results" title="No entries match this search." />
          ) : (
            <EmptyState variant="first-run" title="Nothing has been logged yet." />
          ))}

        {result && result.entries.length > 0 && (
          <>
            {/* The comment is shown whole on a card and truncated in a cell —
                one column definition, one component, which decides for itself
                at the breakpoint (see `AuditComment`). */}
            <DataTable
              caption="Audit log"
              columns={columns}
              rows={result.entries}
              rowKey={(entry) => entry.id}
              cardTitle={(entry) => formatDateTime(entry.createdAt)}
            />

            <div className={surfaces.pager}>
              <Button
                appearance="secondary"
                disabled={result.page <= 1}
                onClick={() => setView((current) => ({ ...current, page: current.page - 1 }))}
              >
                Previous
              </Button>
              <Caption1 className={styles.meta}>
                {`Page ${String(result.page)} of ${String(totalPages)} · ${String(result.total)} entries`}
              </Caption1>
              <Button
                appearance="secondary"
                disabled={result.page >= totalPages}
                onClick={() => setView((current) => ({ ...current, page: current.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

/**
 * Long comments are truncated in the cell, with the whole text on hover.
 *
 * `relationship="label"` also puts it on the trigger's accessible name, so a
 * screen reader gets the full comment rather than the clipped preview — and the
 * trigger carries `role="note"`, because ARIA does not allow naming a bare
 * generic element and the name would simply be dropped.
 *
 * Below the breakpoint the comment is shown whole instead. `DataTable` renders
 * one column definition in both faces, so the decision moved in here from the
 * page's two hand-written branches: a table cell that reflows around 2000
 * characters is unreadable, while a card has a column of its own to wrap into
 * and no hover to reveal the rest with.
 */
function AuditComment({ entry }: { entry: AuditEntry }) {
  const isNarrow = useIsNarrowViewport();
  const comment = entry.comment ?? '';
  if (isNarrow || comment.length <= COMMENT_PREVIEW_CHARS) return <>{comment}</>;
  return (
    <Tooltip content={comment} relationship="label" withArrow>
      <span role="note">{`${comment.slice(0, COMMENT_PREVIEW_CHARS)}…`}</span>
    </Tooltip>
  );
}

/**
 * Wireframe A4's Request column (#156) — the human PR-#### reference,
 * clickable straight to the request, the same `navigate(`/requests/${id}`)`
 * pattern the Dashboard's "View request" menu item uses (#152). Admin-side
 * actions (reviewer/administrator add or remove, a setting change) belong to
 * no request at all, and read as an em dash rather than a broken link.
 */
function RequestCell({ entry, onOpen }: { entry: AuditEntry; onOpen: (path: string) => void }) {
  if (!entry.requestId || entry.requestNo === null) return <>—</>;
  const requestId = entry.requestId;
  return (
    <Link as="button" onClick={() => onOpen(`/requests/${requestId}`)}>
      {`PR-${String(entry.requestNo)}`}
    </Link>
  );
}

/**
 * Saves a fetched blob to disk. The export route is authenticated, so the
 * bytes arrive through `fetch` (as attachment downloads already do) rather than
 * a plain link the browser could follow on its own.
 */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The three numeric settings, held as text so a half-typed value is not a crash. */
interface SettingsForm {
  draftExpiryDays: string;
  maxAttachmentSizeMb: string;
  maxAttachmentsPerRequest: string;
}

type SettingsState =
  | { phase: 'loading' }
  | { phase: 'ready'; settings: AppSettings }
  | { phase: 'error'; error: unknown };

/**
 * Megabytes for people, bytes for the API. Trimmed rather than rounded to a
 * whole number, so a limit that is not an exact multiple of a megabyte still
 * round-trips through the form instead of silently changing itself.
 */
function toMegabytes(bytes: number): string {
  return Number((bytes / BYTES_PER_MB).toFixed(2)).toString();
}

function toForm(settings: AppSettings): SettingsForm {
  return {
    draftExpiryDays: String(settings.draftExpiryDays),
    maxAttachmentSizeMb: toMegabytes(settings.maxAttachmentSizeBytes),
    maxAttachmentsPerRequest: String(settings.maxAttachmentsPerRequest),
  };
}

function fieldError(
  raw: string,
  bounds: { min: number; max: number },
  { wholeNumbers } = { wholeNumbers: true },
): string | undefined {
  const value = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(value)) return 'Enter a number';
  if (wholeNumbers && !Number.isInteger(value)) return 'Whole numbers only';
  if (value < bounds.min || value > bounds.max) {
    return `Must be between ${String(bounds.min)} and ${String(bounds.max)}`;
  }
  return undefined;
}

/**
 * Application settings (#52, wireframe A3).
 *
 * Only the fields that actually changed are sent — the API writes one audit row
 * per change, so restating a value at its current setting would put noise in a
 * trail people read to find real changes. The limits apply from the next
 * request onwards; nothing here needs a deployment or a restart.
 */
function AppSettingsSection() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const responsive = useResponsiveStyles();
  const [state, setState] = useState<SettingsState>({ phase: 'loading' });
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const settings = await getAppSettings();
      setState({ phase: 'ready', settings });
      setForm(toForm(settings));
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const errors = form && {
    draftExpiryDays: fieldError(form.draftExpiryDays, SETTING_BOUNDS.draftExpiryDays),
    // Megabytes are allowed a fraction; the byte value the API stores is not.
    maxAttachmentSizeMb: fieldError(form.maxAttachmentSizeMb, SETTING_BOUNDS.maxAttachmentSizeMb, {
      wholeNumbers: false,
    }),
    maxAttachmentsPerRequest: fieldError(
      form.maxAttachmentsPerRequest,
      SETTING_BOUNDS.maxAttachmentsPerRequest,
    ),
  };
  const hasErrors = errors ? Object.values(errors).some(Boolean) : false;

  function edit(field: keyof SettingsForm, value: string) {
    setSaved(false);
    setForm((current) => (current ? { ...current, [field]: value } : current));
  }

  /** Just the settings whose value differs from what the server last told us. */
  function pendingChanges(settings: AppSettings, current: SettingsForm): AppSettingsUpdate {
    const update: AppSettingsUpdate = {};
    const draftExpiryDays = Number(current.draftExpiryDays);
    if (draftExpiryDays !== settings.draftExpiryDays) update.draftExpiryDays = draftExpiryDays;

    const maxAttachmentSizeBytes = Math.round(Number(current.maxAttachmentSizeMb) * BYTES_PER_MB);
    if (maxAttachmentSizeBytes !== settings.maxAttachmentSizeBytes) {
      update.maxAttachmentSizeBytes = maxAttachmentSizeBytes;
    }

    const maxAttachmentsPerRequest = Number(current.maxAttachmentsPerRequest);
    if (maxAttachmentsPerRequest !== settings.maxAttachmentsPerRequest) {
      update.maxAttachmentsPerRequest = maxAttachmentsPerRequest;
    }
    return update;
  }

  async function handleSave() {
    if (state.phase !== 'ready' || !form || hasErrors) return;
    const update = pendingChanges(state.settings, form);
    setSaving(true);
    setSaveError(null);
    try {
      const settings = await updateAppSettings(update);
      setState({ phase: 'ready', settings });
      setForm(toForm(settings));
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="App settings"
      description="Changes apply immediately — no deployment or restart — and each one is recorded in the audit log with its previous value."
    >
      <div className={layout.stack}>
        {state.phase === 'loading' && (
          // Two-second rule: this is a network call on the render path.
          <Spinner label="Loading settings…" labelPosition="after" />
        )}

        {state.phase === 'error' && (
          <ApiErrorMessage
            error={state.error}
            title="Could not load settings"
            onRetry={() => void load()}
          />
        )}

        {state.phase === 'ready' && form && errors && (
          <>
            <div className={mergeClasses(styles.settingsFields, responsive.singleColumn)}>
              <Field
                label="Draft expiry (days)"
                hint="How long an untouched draft survives before it is removed."
                className={styles.settingsField}
                validationState={errors.draftExpiryDays ? 'error' : 'none'}
                validationMessage={errors.draftExpiryDays}
              >
                <Input
                  type="number"
                  value={form.draftExpiryDays}
                  onChange={(_, data) => edit('draftExpiryDays', data.value)}
                />
              </Field>
              <Field
                label="Maximum file size (MB)"
                hint="Largest single attachment a requester may upload."
                className={styles.settingsField}
                validationState={errors.maxAttachmentSizeMb ? 'error' : 'none'}
                validationMessage={errors.maxAttachmentSizeMb}
              >
                <Input
                  type="number"
                  value={form.maxAttachmentSizeMb}
                  onChange={(_, data) => edit('maxAttachmentSizeMb', data.value)}
                />
              </Field>
              <Field
                label="Maximum files per request"
                hint="How many attachments one request may carry."
                className={styles.settingsField}
                validationState={errors.maxAttachmentsPerRequest ? 'error' : 'none'}
                validationMessage={errors.maxAttachmentsPerRequest}
              >
                <Input
                  type="number"
                  value={form.maxAttachmentsPerRequest}
                  onChange={(_, data) => edit('maxAttachmentsPerRequest', data.value)}
                />
              </Field>
            </div>

            {/* The Settings tab's single `appearance="primary"`; the connection
                panel below deliberately keeps its own action secondary. */}
            <ActionBar
              primary={{ label: 'Save', onClick: () => void handleSave(), disabled: hasErrors }}
              busy={saving}
            />

            {saved && (
              <MessageBar intent="success">
                <MessageBarBody>Settings saved.</MessageBarBody>
              </MessageBar>
            )}

            {saveError && (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Could not save settings</MessageBarTitle>
                  {saveError}
                </MessageBarBody>
              </MessageBar>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

type D365StatusState =
  | { phase: 'loading' }
  | { phase: 'ready'; status: D365Status }
  | { phase: 'error'; error: unknown };

/**
 * Health as a `Badge`, never as coloured text (#202).
 *
 * `colorPalette*` is only permitted inside a Fluent `MessageBar` intent or a
 * `Badge` colour — a hand-tinted `<span>` is a banner the theme cannot reason
 * about. And the word is what actually carries the state: high contrast
 * collapses every palette hue to a system colour, so "Healthy"/"Unreachable"
 * has to be legible with no colour at all.
 */
function HealthBadge({ healthy }: { healthy: boolean }) {
  return (
    <Badge appearance="tint" color={healthy ? 'success' : 'danger'}>
      {healthy ? 'Healthy' : 'Unreachable'}
    </Badge>
  );
}

/**
 * D365 connection (#52/#192, wireframe A3) — read-only by design: the
 * credentials are deployment configuration, not settings, and this panel
 * exists to answer "is it working right now?" rather than to change anything.
 *
 * Two independent sources feed it. The always-visible facts above (#192) —
 * environment, service account status, last master-data call — cost nothing
 * to show: they are what the server already knows, no live D365 call. "Test
 * connection" below is the one thing that makes a real call, and stays
 * button-gated: firing it on every render of the Admin tab would spend the
 * shared service account's throttle budget to answer a question nobody asked.
 */
function D365ConnectionPanel() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const [statusState, setStatusState] = useState<D365StatusState>({ phase: 'loading' });
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<D365ConnectionResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setStatusState({ phase: 'loading' });
    try {
      setStatusState({ phase: 'ready', status: await getD365Status() });
    } catch (err) {
      setStatusState({ phase: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function handleTest() {
    setTesting(true);
    setFailure(null);
    try {
      setResult(await testD365Connection());
      // A test call can itself have refreshed the cached token — re-read the
      // always-visible facts so "token valid N min" reflects it.
      void loadStatus();
    } catch (err) {
      // The endpoint reports an unhealthy D365 as a 200 — reaching here means
      // our own API did not answer, which is a different problem entirely.
      setResult(null);
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  }

  return (
    <Section
      title="D365 connection"
      description="Read-only. The service account and environment URL come from deployment configuration."
    >
      <div className={layout.stack}>
        {statusState.phase === 'loading' && (
          // Two-second rule: this is a network call on the render path.
          <Spinner size="tiny" label="Loading connection facts…" labelPosition="after" />
        )}

        {statusState.phase === 'error' && (
          <ApiErrorMessage
            error={statusState.error}
            title="Could not load the D365 connection facts"
            onRetry={() => void loadStatus()}
          />
        )}

        {statusState.phase === 'ready' && (
          <div className={styles.facts}>
            <div className={styles.fact}>
              <Caption1 className={styles.meta}>Environment</Caption1>
              <Body1>{statusState.status.environment}</Body1>
            </div>
            <div className={styles.fact}>
              <Caption1 className={styles.meta}>Service account</Caption1>
              <Body1>
                {statusState.status.serviceAccount.connected
                  ? `Connected · token valid ${String(statusState.status.serviceAccount.tokenValidForMinutes)} min`
                  : 'Not connected yet'}
              </Body1>
              <HealthBadge healthy={statusState.status.serviceAccount.connected} />
            </div>
            <div className={styles.fact}>
              <Caption1 className={styles.meta}>Last master-data call</Caption1>
              <Body1>
                {statusState.status.lastMasterDataCall
                  ? `${formatDateTime(statusState.status.lastMasterDataCall.at)} · ${String(statusState.status.lastMasterDataCall.durationMs)} ms`
                  : 'none yet'}
              </Body1>
            </div>
          </div>
        )}

        <div className={styles.facts}>
          {result === null ? (
            <Body1>Not tested yet.</Body1>
          ) : (
            <>
              <HealthBadge healthy={result.healthy} />
              <Caption1 className={styles.meta}>
                {`Last call ${String(result.latencyMs)} ms · tested ${formatDateTime(result.testedAt)}`}
              </Caption1>
              {/* The badge above already carries the outcome, so the verbatim
                  D365 message stays de-emphasised metadata rather than a
                  second, hand-tinted red thing competing with it. */}
              {result.error && <Caption1 className={styles.meta}>{result.error}</Caption1>}
            </>
          )}
        </div>

        <ActionBar
          secondary={
            <Button appearance="secondary" onClick={() => void handleTest()}>
              {testing ? (
                <Spinner size="tiny" label="Testing…" labelPosition="after" />
              ) : (
                'Test connection'
              )}
            </Button>
          }
          busy={testing}
        />

        {failure && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not run the connection test</MessageBarTitle>
              {failure}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
    </Section>
  );
}

/** Debounced server-side directory search — the picker never accepts free text (#50). */
function useDirectorySearch(query: string) {
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState<SearchState>({ phase: 'idle' });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debounced.length < SEARCH_MIN_CHARS) {
      setState({ phase: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setState({ phase: 'loading' });
    searchDirectory(debounced)
      .then((results) => {
        if (!cancelled) setState({ phase: 'ready', results });
        return undefined;
      })
      .catch(() => {
        if (!cancelled) setState({ phase: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  return state;
}

/**
 * The Remove action, rendered identically from the table cell and from the
 * mobile card: same disabled reasons, same accessible name, same handler —
 * the presentation changes at the breakpoint, the behaviour never does.
 *
 * Deliberately `secondary` rather than the destructive red: this button opens
 * a confirm dialog, it does not remove anyone, and a column of red buttons is
 * exactly the "repeated emphasis per row" the design system bans. The red is
 * spent once, on the confirm inside the dialog.
 */
function RemoveReviewerButton({
  reviewer,
  disabledReason,
  onRemove,
}: {
  reviewer: Reviewer;
  disabledReason?: string;
  onRemove: () => void;
}) {
  return (
    <Button
      appearance="secondary"
      size="small"
      disabled={disabledReason !== undefined}
      title={disabledReason}
      aria-label={
        disabledReason
          ? `Remove ${reviewer.name} — ${disabledReason}`
          : `Remove ${reviewer.name} from the reviewer group`
      }
      onClick={onRemove}
    >
      Remove
    </Button>
  );
}

/**
 * The Remove action for an administrator, styled and behaved identically to
 * `RemoveReviewerButton` above — same disabled-reason pattern, just for the
 * other membership list (#156).
 */
function RemoveAdministratorButton({
  administrator,
  disabledReason,
  onRemove,
}: {
  administrator: Administrator;
  disabledReason?: string;
  onRemove: () => void;
}) {
  return (
    <Button
      appearance="secondary"
      size="small"
      disabled={disabledReason !== undefined}
      title={disabledReason}
      aria-label={
        disabledReason
          ? `Remove ${administrator.name} — ${disabledReason}`
          : `Remove ${administrator.name} as an administrator`
      }
      onClick={onRemove}
    >
      Remove
    </Button>
  );
}

/**
 * Administrator management (#156, wireframe A1 note 2) — the same add/remove
 * shape as the reviewer group above (search-and-pick from the directory, a
 * confirm dialog on Remove, self-removal and last-one-standing both
 * disabled with a reason), applied to `admin.aadIds` instead of
 * `ReviewerGroupMember`. `admin.aadIds` had been read-only until this ticket:
 * changing who could reach this screen needed an env change and a redeploy.
 */
function AdministratorsSection({ ownAadId }: { ownAadId: string | undefined }) {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();

  const [state, setState] = useState<AdminLoadState>({ phase: 'loading' });
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Administrator | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // K1 "Dialog closes" — back to the Remove control that opened it.
  useRestoreFocusOnClose(removeTarget !== null);
  // K1 "Dialog opens" — same non-destructive-action-gets-focus choice the
  // reviewer removal dialog makes (log entry 21).
  const cancelRemoveRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (removeTarget !== null) cancelRemoveRef.current?.focus();
  }, [removeTarget]);

  const search = useDirectorySearch(query);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const administrators = await getAdministrators();
      setState({ phase: 'ready', administrators });
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAdd(aadId: string) {
    setAdding(true);
    setAddError(null);
    try {
      await addAdministrator(aadId);
      setQuery('');
      await load();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove() {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeAdministrator(removeTarget.aadId);
      setRemoveTarget(null);
      await load();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemoving(false);
    }
  }

  const administrators = state.phase === 'ready' ? state.administrators : [];

  /** The removal rules (A1 note 2), resolved once per administrator and shared by both layouts. */
  function removalProps(administrator: Administrator) {
    const isSelf = ownAadId !== undefined && administrator.aadId === ownAadId;
    const isLast = administrators.length === 1;
    const disabledReason = isSelf
      ? 'You cannot remove your own administrator access'
      : isLast
        ? 'The last administrator cannot be removed'
        : undefined;
    return { administrator, disabledReason, onRemove: () => setRemoveTarget(administrator) };
  }

  const columns: Column<Administrator>[] = [
    { key: 'name', header: 'Name', priority: 2, render: (row) => row.name },
    { key: 'email', header: 'Email', priority: 1, render: (row) => row.email },
  ];

  return (
    <Section
      title="Administrators"
      description="Who can reach this Admin tab. Changing this list takes effect immediately — no deployment involved."
      surface={false}
    >
      <div className={layout.stack}>
        <div className={mergeClasses(surfaces.card, styles.addRow)}>
          <Field
            label="Add an administrator"
            hint="Search the Microsoft 365 directory by name or email"
          >
            <Combobox
              freeform
              // Distinct from the reviewer picker's placeholder below — both
              // pickers render on this screen, and each needs its own
              // accessible way to be found (by a test, or by a screen reader
              // user scanning placeholders).
              placeholder="Start typing a name or email to add an administrator…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onOptionSelect={(_, data) => {
                if (data.optionValue) void handleAdd(data.optionValue);
              }}
            >
              {search.phase === 'ready' &&
                search.results.map((user) => (
                  <Option key={user.aadId} value={user.aadId} text={`${user.name} — ${user.email}`}>
                    {user.name} — {user.email}
                  </Option>
                ))}
            </Combobox>
          </Field>
          {search.phase === 'loading' && (
            <Spinner size="tiny" label="Searching…" labelPosition="after" />
          )}
          {search.phase === 'error' && (
            <Body1 as="p" className={styles.meta}>
              Could not search the directory right now.
            </Body1>
          )}
          {adding && <Spinner size="tiny" label="Adding…" labelPosition="after" />}
          {addError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not add administrator</MessageBarTitle>
                {addError}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>

        {state.phase === 'loading' && (
          // Two-second rule: this is a network call on the render path.
          <Spinner label="Loading administrators…" labelPosition="after" />
        )}

        {state.phase === 'error' && (
          <ApiErrorMessage
            error={state.error}
            title="Could not load administrators"
            onRetry={() => void load()}
          />
        )}

        {/* Below the breakpoint every administrator row becomes a labelled card
            carrying the same Remove button, with the same disabled reasons —
            derived from the columns above rather than declared a second time. */}
        {state.phase === 'ready' && (
          <DataTable
            caption="Administrators"
            columns={columns}
            rows={administrators}
            rowKey={(row) => row.aadId}
            cardTitle={(row) => row.name}
            rowActions={(row) => <RemoveAdministratorButton {...removalProps(row)} />}
          />
        )}
      </div>

      {removeTarget && (
        <Dialog
          open
          onOpenChange={(_, data) => {
            if (!data.open) setRemoveTarget(null);
          }}
        >
          <DialogSurface className={responsive.touchTargets}>
            <DialogBody>
              <DialogTitle>Remove {removeTarget.name} as an administrator?</DialogTitle>
              <DialogContent>
                They will no longer be able to reach this Admin tab. You can add them back later if
                needed.
                {removeError && (
                  <MessageBar intent="error" className={styles.dialogMessage}>
                    <MessageBarBody>{removeError}</MessageBarBody>
                  </MessageBar>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  ref={cancelRemoveRef}
                  appearance="subtle"
                  onClick={() => setRemoveTarget(null)}
                  disabled={removing}
                >
                  Cancel
                </Button>
                <Button
                  appearance="secondary"
                  className={styles.destructiveConfirm}
                  onClick={() => void handleRemove()}
                  disabled={removing}
                >
                  {removing ? <Spinner size="tiny" /> : 'Remove'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </Section>
  );
}

/** The secondary TabList's four panels (#192, wireframe A1/A3). */
type AdminTab = 'reviewers' | 'notifications' | 'settings' | 'audit';

export function AdminPanel() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const { context } = useTeams();
  const ownAadId = context?.user?.id;

  // Reviewers is the default/first tab, matching wireframe A1 — nothing here
  // is routed, since the primary tabs in App.tsx already own the URL and this
  // is one level deeper.
  const [activeTab, setActiveTab] = useState<AdminTab>('reviewers');

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Reviewer | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // K1 "Dialog closes" — back to the Remove control that opened it.
  useRestoreFocusOnClose(removeTarget !== null);
  // K1 "Dialog opens" — focus the non-destructive action, the same choice
  // RequestForm's close-warning dialog makes: "Remove" is the one button
  // here that changes state, so Cancel gets initial focus rather than
  // Fluent's first-focusable default — which under jsdom finds no
  // focusable child at all and falls to the surface instead (log entry 21).
  const cancelRemoveRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (removeTarget !== null) cancelRemoveRef.current?.focus();
  }, [removeTarget]);

  const search = useDirectorySearch(query);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const reviewers = await getReviewers();
      setState({ phase: 'ready', reviewers });
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAdd(aadId: string) {
    setAdding(true);
    setAddError(null);
    try {
      await addReviewer(aadId);
      setQuery('');
      await load();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove() {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeReviewer(removeTarget.aadId);
      setRemoveTarget(null);
      await load();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemoving(false);
    }
  }

  const reviewers = state.phase === 'ready' ? state.reviewers : [];

  /** The removal rules, resolved once per reviewer and shared by both layouts. */
  function removalProps(reviewer: Reviewer) {
    const isSelf = ownAadId !== undefined && reviewer.aadId === ownAadId;
    const isLast = reviewers.length === 1;
    const disabledReason = isSelf
      ? 'You cannot remove your own reviewer membership'
      : isLast
        ? 'The last reviewer cannot be removed'
        : undefined;
    return { reviewer, disabledReason, onRemove: () => setRemoveTarget(reviewer) };
  }

  const reviewerColumns: Column<Reviewer>[] = [
    { key: 'name', header: 'Name', priority: 3, render: (row) => row.name },
    { key: 'email', header: 'Email', priority: 2, render: (row) => row.email },
    {
      key: 'decisions',
      header: 'Decisions made',
      numeric: true,
      priority: 1,
      render: (row) => row.decisionsCount,
    },
  ];

  return (
    <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
      {/* The page's one `<h1>`, and the only one on this route. The secondary
          TabList is the header's `children` row — one level under the primary
          My Requests / Reviewer Queue / Admin tabs in App.tsx. */}
      <PageHeader title="Admin">
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => setActiveTab(data.value as AdminTab)}
        >
          <Tab value="reviewers">Reviewers</Tab>
          <Tab value="notifications">Notifications</Tab>
          <Tab value="settings">Settings</Tab>
          <Tab value="audit">Audit log</Tab>
        </TabList>
      </PageHeader>

      {/* Reviewers — the review group table plus the Administrators table,
          both on the same A1 frame. */}
      {activeTab === 'reviewers' && (
        <div className={layout.stack}>
          <Section title="Reviewer group" surface={false}>
            <div className={layout.stack}>
              <div className={mergeClasses(surfaces.card, styles.addRow)}>
                <Field
                  label="Add a reviewer"
                  hint="Search the Microsoft 365 directory by name or email"
                >
                  <Combobox
                    freeform
                    placeholder="Start typing a name or email…"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) void handleAdd(data.optionValue);
                    }}
                  >
                    {search.phase === 'ready' &&
                      search.results.map((user) => (
                        <Option
                          key={user.aadId}
                          value={user.aadId}
                          text={`${user.name} — ${user.email}`}
                        >
                          {user.name} — {user.email}
                        </Option>
                      ))}
                  </Combobox>
                </Field>
                {search.phase === 'loading' && (
                  <Spinner size="tiny" label="Searching…" labelPosition="after" />
                )}
                {search.phase === 'error' && (
                  <Body1 as="p" className={styles.meta}>
                    Could not search the directory right now.
                  </Body1>
                )}
                {adding && <Spinner size="tiny" label="Adding…" labelPosition="after" />}
                {addError && (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      <MessageBarTitle>Could not add reviewer</MessageBarTitle>
                      {addError}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </div>

              {state.phase === 'loading' && (
                // Two-second rule: this is a network call on the render path.
                <Spinner label="Loading reviewers…" labelPosition="after" />
              )}

              {state.phase === 'error' && (
                <ApiErrorMessage
                  error={state.error}
                  title="Could not load reviewers"
                  onRetry={() => void load()}
                />
              )}

              {/* Below the breakpoint every reviewer row becomes a labelled card
                  carrying the same Remove button, with the same disabled reasons. */}
              {state.phase === 'ready' && (
                <DataTable
                  caption="Reviewers"
                  columns={reviewerColumns}
                  rows={reviewers}
                  rowKey={(row) => row.id}
                  cardTitle={(row) => row.name}
                  rowActions={(row) => <RemoveReviewerButton {...removalProps(row)} />}
                />
              )}
            </div>
          </Section>

          <AdministratorsSection ownAadId={ownAadId} />
        </div>
      )}

      {activeTab === 'notifications' && <NotificationTemplatesSection />}

      {activeTab === 'settings' && (
        <div className={layout.stack}>
          <AppSettingsSection />
          <D365ConnectionPanel />
        </div>
      )}

      {activeTab === 'audit' && <AuditLogSection />}

      {removeTarget && (
        <Dialog
          open
          onOpenChange={(_, data) => {
            if (!data.open) setRemoveTarget(null);
          }}
        >
          <DialogSurface className={responsive.touchTargets}>
            <DialogBody>
              <DialogTitle>Remove {removeTarget.name} from the reviewer group?</DialogTitle>
              <DialogContent>
                They will no longer see or act on requests in the reviewer queue. You can add them
                back later if needed.
                {removeError && (
                  <MessageBar intent="error" className={styles.dialogMessage}>
                    <MessageBarBody>{removeError}</MessageBarBody>
                  </MessageBar>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  ref={cancelRemoveRef}
                  appearance="subtle"
                  onClick={() => setRemoveTarget(null)}
                  disabled={removing}
                >
                  Cancel
                </Button>
                <Button
                  appearance="secondary"
                  className={styles.destructiveConfirm}
                  onClick={() => void handleRemove()}
                  disabled={removing}
                >
                  {removing ? <Spinner size="tiny" /> : 'Remove'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}
